// cspell:ignore relock
/**
 * Diagnostic and plumbing knobs: thumbnail timeouts, ingestion routing, engine
 * id overrides, the raw featureFlags blob, the folder-view display variants,
 * and the Folders v2 force-on override.
 *
 * NOT EXPECTED TO BE REGISTERED, and that is what separates this file from
 * `advanced.ts` rather than how obscure the knob is. An environment without
 * these is working as intended, so the page shows an absent one as "Not
 * registered" instead of Missing and offers no bulk register on the tab. A key
 * that SHOULD be in every environment belongs in `advanced.ts`, however rarely
 * anyone touches it.
 *
 * Registered deliberately, they are diffed like anything else — the wrong type
 * or default on one of these is as drifted as on any other key.
 *
 * Answering "should I change this?" for anything here generally needs the
 * source: `relockSleep` is read only through the featureFlags blob,
 * `thumbnailRequestTimeout` is String-not-Integer for a reason three layers
 * down, and the engine ids declare no default so the app's built-in wins.
 *
 * `folderViewTiles`, `folderViewTilesUser` and `folderViewUser` are here while
 * `folderView` itself is standard, which is deliberate: the main switch is the
 * decision, and these three are display fine-tuning on top of it — including
 * the only two `configLevel: 'User'` definitions in the manifest, which is a
 * distinction a reader scanning a list does not expect and the row flags with
 * its own pill.
 *
 * FULL DEFINITIONS, diffed exactly like `standard.ts`. The tier decides which
 * tab lists the row, nothing more.
 */

import { ManifestDefinition } from '@redact-modules/admin/models';
export const DEVELOPER_DEFINITIONS: readonly ManifestDefinition[] = [
  {
    // VE-31036: moved here from `advanced.ts`. It is NOT the signal for whether
    // an org is on Folders v2 — that is the org metadata
    // (`jsondata.features.v2FoldersEnabled`, set via updateOrganization), which
    // the app reads through `selectV2FoldersEnabled`. This key can only force
    // the v2 paths ON, for exercising them against an org whose metadata is not
    // set, so an environment without it is working as intended. Expected to be
    // retired once Folders v2 is universal.
    //
    // CLIENT-SIDE ONLY, which matters when using it as intended: the BFF reads
    // the org metadata and nothing else (server `getOrgV2FoldersEnabled`), so
    // turning this on for an org whose metadata is unset puts the client on v2
    // while BFF-mediated case/folder create and delete stay on v1. That split
    // is the thing the server-side adapter exists to avoid, so treat it as a
    // local debugging tool, not a way to migrate an org.
    configKey: 'v2FoldersEnabled',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Developer override: force the Folders v2 code paths on. Org metadata is the real signal; this can only force on, never off.',
    defaultValue: 'false',
  },
  {
    configKey: 'disableFastPdfIngestion',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: Route PDFs through the converter engine instead of fast ingestion',
    defaultValue: 'false',
  },
  {
    // Kill switch for the Redact-side `original-media` write on video,
    // audio and image uploads (VE-30513). Off by default so the app-side
    // attach is live; flip on to roll back to the ingestor-only write per
    // org without a Redact deploy. DEPRECATE once the new approach has
    // proven itself in production — see `user-permissions/selectors.ts`.
    configKey: 'disableOriginalMediaAttach',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: Skip attaching the original-media asset for video, audio and image uploads from Redact; leave it to the GLC ingestor. Turning this on puts the signed upload URL back in the ingestion task payload.',
    defaultValue: 'false',
  },
  {
    /**
     * STRING, NOT INTEGER, and deliberately.
     *
     * `appConfigValue` parses an Integer definition with `parseInt`, and
     * `selectApplicationConfigs` then drops every falsy resolved value — so a
     * timeout an org deliberately set to `0` would never reach `appConfigs`, and the
     * `?? 0 is valid setting` chain in user-permissions/selectors.ts could not
     * honour it. A String resolves to `"0"`, which is truthy, survives the drop, and
     * `Number()`s back to 0.
     */
    configKey: 'thumbnailRequestTimeout',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Redact: Milliseconds before a thumbnail request times out',
    defaultValue: '10000',
  },
  {
    /**
     * String for the same reason as `thumbnailRequestTimeout` above.
     *
     * Stage us-gov-2 has this registered as Integer, so the page reports drift on
     * it. That drift is real and the environment is the side that is wrong.
     */
    configKey: 'thumbnailRunningQueueTimeout',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Redact: Milliseconds before a running thumbnail queue entry times out',
    defaultValue: '20000',
  },
  {
    /**
     * No default, like `transcriptionEngineId`: `engines/selectors.ts` reads it from
     * `applicationConfigs` and falls back to an engine id hard-coded in the app, so
     * registering it with no default leaves that built-in id in charge.
     */
    configKey: 'voiceMaskEngineId',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Redact: Engine Id to use for Voice Mask. Overrides the engine id built into the app; leave unset to use that.',
  },
  {
    /**
     * No default, for the same reason as `voiceMaskEngineId` above. The
     * built-in id is version-matched to the transcription engine beside it in
     * `utils/speechmatics/engines.ts`, so the two move together; a default
     * pinned here
     * would have to be changed in two places every time they do.
     */
    configKey: 'speakerSeparationEngineId',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Redact: Engine Id to use for Speaker Separation. Overrides the engine id built into the app; leave unset to use that.',
  },
  {
    /**
     * No default, for the same reason as `voiceMaskEngineId` above.
     */
    configKey: 'detectionEngineGPU',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Redact: GPU detection Engine Id. Overrides the engine id built into the app; leave unset to use that.',
  },
  {
    /**
     * NOTHING READS A CONFIG OF THIS NAME.
     *
     * user-permissions/selectors.ts reads `featureFlags.relockSleep || 500` — the
     * blob, not a key of its own — so a definition for the bare key is consulted by
     * nothing. It is listed here so it has a home and a tab rather than surfacing as
     * Unrecognized, but be clear about what that costs: it is reported Missing on
     * every environment that has not registered it, and registering it changes no
     * behaviour. Set it through the `featureFlags` JSON config instead.
     */
    configKey: 'relockSleep',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Redact: Milliseconds to wait before re-locking a file after an edit.',
  },
  {
    /**
     * THE BLOB EVERY OTHER FLAG FALLS BACK TO, which makes registering it the
     * widest-reaching row on the page: `booleanAppConfig` consults a registered
     * definition before this, so a key that exists in both stops being decided here.
     */
    configKey: 'featureFlags',
    configType: 'JSON',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Redact: Feature Flags',
  },
  {
    configKey: 'folderViewTiles',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      "Feature Flag: Thumbnail tile (grid) mode for a request's files in the folder view (default enabled; set false to disable)",
    defaultValue: 'true',
  },
  {
    /**
     * `configLevel: 'User'` — set per user, so an org-level change does not reach
     * it and two people in the same org can see different behaviour.
     */
    configKey: 'folderViewTilesUser',
    configType: 'Boolean',
    configLevel: 'User',
    required: false,
    secured: false,
    description:
      "Feature Flag: Thumbnail tile (grid) mode for a request's files in the folder view (per user)",
    defaultValue: 'false',
  },
  {
    /**
     * `configLevel: 'User'`, like `folderViewTilesUser`.
     */
    configKey: 'folderViewUser',
    configType: 'Boolean',
    configLevel: 'User',
    required: false,
    secured: false,
    description: 'Feature Flag: Use Redact Folder View for an individual user',
    defaultValue: 'false',
  },
  {
    configKey: 'enableWatermarks',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Watermarks (default on)',
    defaultValue: 'true',
  },
  {
    configKey: 'enableBatesStamping',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Bates stamping (default on)',
    defaultValue: 'true',
  },
  {
    configKey: 'enablePageDeletion',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: Delete a page from the burned-in PDF (default on)',
    defaultValue: 'true',
  },
  {
    configKey: 'enableRedactPage',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: Redact Page tool in the document editor - one redaction per element on the page (default off)',
    defaultValue: 'false',
  },
  {
    // Kill switch for the first-playback auto-engage only (VE-25876), not for
    // the "On screen only" toggle it drives — the toggle survives this being
    // off. Default `true` and read through `booleanAppConfig`, so registering
    // it at `'false'` would hard-disable the behaviour everywhere; see the
    // header of `standard.ts` on which reader decides.
    configKey: 'autoEngageOnScreenFilter',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: Engage the cluster panel on-screen-only filter automatically the first time a video is played (default on)',
    defaultValue: 'true',
  },
  {
    configKey: 'bulkEffects',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: Re-enable the old redaction effects menu for bulk custom redactions',
    defaultValue: 'false',
  },
];
